import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function HowItWorksPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'How Automigo Works — Lead Automation for Long Island Businesses',
    description: 'Step-by-step explanation of how Automigo automates lead responses, follow-ups, and notifications for local service businesses on Long Island.',
    url: `${siteUrl}/how-it-works`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'How It Works', item: `${siteUrl}/how-it-works` }
      ]
    },
    mainEntity: {
      '@type': 'HowTo',
      name: 'How Automigo Automates Your Lead Response',
      description: 'A simple 4-step process to automate customer inquiry responses for local service businesses.',
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Customer Submits Request',
          text: 'Visitor fills out your normal quote/contact form and instantly receives confirmation.'
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: "You're Notified Immediately",
          text: 'You receive the new lead right away via text message, email, or both.'
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'Automatic Follow-Up',
          text: "If the customer doesn't respond, Automigo checks back automatically so the lead doesn't go cold."
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'You Reply Normally',
          text: 'Respond from your phone or email just like any conversation. You stay in control.'
        }
      ]
    }
  };

  return (
    <>
      <SEOHead
        title="How It Works | Automigo Lead Automation for Long Island Businesses"
        description="Learn how Automigo's automated lead response system works. From instant SMS replies to smart follow-ups and lead tracking — see how I help Long Island businesses win more jobs."
        keywords="how lead automation works, SMS automation process, automated lead response system, business automation steps, lead tracking Long Island"
        canonical={`${siteUrl}/how-it-works`}
        schema={schema}
      />

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-center justify-between h-20">
              <div>
                <Link to="/" className="flex items-center gap-3 cursor-pointer">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                    <i className="ri-robot-2-line text-white text-xl"></i>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">Automigo</span>
                </Link>
                <p className="text-xs text-gray-600 mt-1 ml-13">Serving Long Island Businesses</p>
              </div>
              <div className="hidden md:flex items-center gap-8">
                <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
                <Link to="/how-it-works" className="text-sm font-medium text-teal-600 cursor-pointer whitespace-nowrap">How It Works</Link>
                <Link to="/who-its-for" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Who It's For</Link>
                <Link to="/about-us" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">About</Link>
                <Link to="/#contact" className="bg-teal-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer text-sm whitespace-nowrap">Get Started</Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="max-w-4xl mx-auto px-6 lg:px-12 relative z-10 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              How Automigo Works
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Automigo upgrades your existing request form so customers receive immediate responses and you're notified right away. Nothing changes for your customers.
            </p>
          </div>
        </section>

        {/* Steps Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <div className="space-y-16">
              {/* Step 1 */}
              <div className="bg-gray-50 rounded-2xl p-10">
                <div className="flex items-start gap-6 mb-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center">
                    <i className="ri-file-list-3-line text-white text-3xl"></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-teal-600 mb-2">STEP 1</div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Customer Submits Request</h2>
                    <p className="text-lg text-gray-700 mb-6">Visitor fills out your normal quote/contact form</p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <p className="text-gray-700">They instantly receive confirmation</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <p className="text-gray-700">They know you're available</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <p className="text-gray-700">They stop searching competitors</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-gray-50 rounded-2xl p-10">
                <div className="flex items-start gap-6 mb-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center">
                    <i className="ri-notification-3-line text-white text-3xl"></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-teal-600 mb-2">STEP 2</div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">You're Notified Immediately</h2>
                    <p className="text-lg text-gray-700 mb-6">You receive the new lead right away</p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-teal-600 rounded-full flex-shrink-0 mt-2"></div>
                        <p className="text-gray-700">Text message</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-teal-600 rounded-full flex-shrink-0 mt-2"></div>
                        <p className="text-gray-700">Email</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-teal-600 rounded-full flex-shrink-0 mt-2"></div>
                        <p className="text-gray-700">Or both</p>
                      </div>
                    </div>
                    <p className="text-gray-600 mt-6 italic">No dashboard monitoring required</p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-gray-50 rounded-2xl p-10">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center">
                    <i className="ri-repeat-line text-white text-3xl"></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-teal-600 mb-2">STEP 3</div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Automatic Follow-Up</h2>
                    <p className="text-lg text-gray-700">
                      If the customer doesn't respond, Automigo checks back automatically so the lead doesn't go cold.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="bg-gray-50 rounded-2xl p-10">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center">
                    <i className="ri-message-2-line text-white text-3xl"></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-teal-600 mb-2">STEP 4</div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">You Reply Normally</h2>
                    <p className="text-lg text-gray-700">
                      Respond from your phone or email just like any conversation. You stay in control — Automigo handles the busywork.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Compliance Section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <div className="bg-white rounded-2xl p-10 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center">
                  <i className="ri-shield-check-line text-teal-600 text-3xl"></i>
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Clear Communication</h2>
                  <p className="text-lg text-gray-700">
                    Your form includes messaging disclosures and privacy details so customers understand how communication works.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Typical Setup</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-teal-50 rounded-xl p-8 text-center">
                <div className="text-4xl font-bold text-teal-600 mb-3">Day 1</div>
                <p className="text-gray-700 font-medium">Connect form</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-8 text-center">
                <div className="text-4xl font-bold text-teal-600 mb-3">Day 2</div>
                <p className="text-gray-700 font-medium">Customize responses</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-8 text-center">
                <div className="text-4xl font-bold text-teal-600 mb-3">Day 3</div>
                <p className="text-gray-700 font-medium">Go live</p>
              </div>
            </div>
            <p className="text-center text-gray-600 mt-8 text-lg">Most businesses run within a few days.</p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-teal-600 to-cyan-500">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Ready to see how it would work for your business?
            </h2>
            <Link 
              to="/#contact"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-lg"
            >
              Get My Free Automation Plan
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-8">
              <p className="text-gray-400 text-sm">Serving businesses across Long Island, NY</p>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-robot-2-line text-white text-xl"></i>
                </div>
                <span className="text-2xl font-bold text-white">Automigo</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-6">
                <Link to="/about-us" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">About</Link>
                <Link to="/how-it-works" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">How It Works</Link>
                <Link to="/#contact" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">Contact</Link>
                <Link to="/terms-and-conditions" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">Messaging Terms</Link>
                <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">Privacy</Link>
              </div>
              <div className="flex items-center gap-6">
                <p className="text-sm text-gray-500">© 2025 Automigo</p>
                <a href="https://readdy.ai/?ref=logo" className="text-sm text-gray-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
